import { useState } from 'react';

import {
  Button,
  Column,
  Row,
} from '../../../components';
import TotpEnrollScan from '../../../components/secondFactor/TotpEnrollScan';
import {
  useAuth,
  useTownAssets,
} from '../../../stores';
import { config } from '../../../utils';
import secondFactorLabelOk from '../../../utils/secondFactor/secondFactorLabelOk';
import webauthnDefaultLabel from '../../../utils/secondFactor/webauthnDefaultLabel';
import webauthnKeyGesture from '../../../utils/secondFactor/webauthnKeyGesture';
import webauthnRefusalKey from '../../../utils/secondFactor/webauthnRefusalKey';
import webauthnSupported from '../../../utils/secondFactor/webauthnSupported';

function LoginStepEnroll() {
  const { auth } = useAuth();
  const { assets } = useTownAssets();
  const { translate } = assets;
  const [label, setLabel] = useState(() => translate('totp', 'secondFactorMethod'));
  const [enrollment, setEnrollment] = useState();
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState();

  const allowed = auth.server.loginPending?.methods?.allowed ?? ['totp'];
  const appOffered = allowed.includes('totp');
  const keyOffered = (allowed.includes('localKey') || allowed.includes('cloudPasskey')) && webauthnSupported();

  const trimmedLabel = label.trim();
  const codeSendable = secondFactorLabelOk(trimmedLabel) && /^[0-9]{6}$/.test(code) && (!pending);

  const add = async () => {
    if (pending) return;

    setPending(true);
    setRefusal();

    const answer = await auth.server.loginStep(
      config.town.back.auth.loginStep.totpAdd,
      { label: trimmedLabel },
    );

    setPending(false);
    if (answer.userTotpId) {
      // The server numbers a name the account already uses
      setLabel(answer.label);
      setEnrollment(answer);
    } else if (answer.tooMany) setRefusal('secondFactorTooManyApps');
    else if (!answer.loginPendingExpired) setRefusal('reqFailed');
  };

  const addKey = async (kind) => {
    if (pending) return;

    setPending(true);
    setRefusal();

    const options = await auth.server.loginStep(config.town.back.auth.loginStep.webauthnOptions, { kind });
    const gesture = (options.options)
      ? await webauthnKeyGesture('register', options.options)
      : { cancelled: true };

    if (gesture.cancelled) {
      setPending(false);
      if (!options.loginPendingExpired) setRefusal('secondFactorKeyCancelled');
      return;
    }

    const answer = await auth.server.loginStep(
      config.town.back.auth.loginStep.webauthnAdd,
      { label: webauthnDefaultLabel(kind, translate), response: gesture.response },
    );

    // The next step replaces this form, which must not touch its state any more
    if (answer.done || answer.steps) return;

    setPending(false);
    if (!answer.loginPendingExpired) setRefusal(webauthnRefusalKey(answer));
  };

  const confirm = async () => {
    if (!codeSendable) return;

    setPending(true);
    setRefusal();

    const answer = await auth.server.loginStep(
      config.town.back.auth.loginStep.totpConfirm,
      { userTotpId: enrollment.userTotpId, code, label: trimmedLabel },
    );

    // The next step replaces this form, which must not touch its state any more
    if (answer.done || answer.steps) return;

    setPending(false);
    if (answer.badCode) {
      setRefusal('secondFactorBadCode');
      setCode('');
    } else if (!answer.loginPendingExpired) setRefusal('reqFailed');
  };

  return (
    <Column gap={4} className="max-w-120">
      <span className="font-semibold">{translate('twoFactorLogin')}</span>

      {(enrollment)
        ? (
            <TotpEnrollScan
              uri={enrollment.uri}
              secret={enrollment.secret}
              label={label}
              onLabelChange={(newLabel) => {
                setLabel(newLabel);
                setRefusal();
              }}
              code={code}
              onCodeChange={(newCode) => {
                setCode(newCode);
                setRefusal();
              }}
              onEnter={confirm}
              disabled={pending}
            />
          )
        : (
            <>
              {(appOffered)
                ? <span className="text-center">{translate('secondFactorEnrollIntro', 'statements')}</span>
                : null}

              {(keyOffered)
                ? (
                    <span className="text-center">
                      {translate(
                        (appOffered) ? 'secondFactorEnrollKeyIntro' : 'secondFactorEnrollKeyOnlyIntro',
                        'statements',
                      )}
                    </span>
                  )
                : null}
            </>
          )}

      {(refusal)
        ? <span className="text-pxUiAlert">{translate(refusal, 'statements')}</span>
        : <span>{' '}</span>}

      <Row gap={6}>
        <Button
          label={translate('cancel')}
          onClick={auth.server.loginStepCancel}
          neutralColor
        />

        {(enrollment)
          ? (
              <Button
                label={translate('confirm')}
                onClick={confirm}
                disabled={!codeSendable}
                pending={pending}
                baseColor
              />
            )
          : (
              <>
                {(appOffered)
                  ? (
                      <Button
                        label={((keyOffered) ? translate('secondFactorUseApp', 'statements') : translate('next'))}
                        onClick={add}
                        disabled={pending}
                        pending={pending}
                        baseColor
                      />
                    )
                  : null}

                {(keyOffered)
                  ? (
                      <>
                        <Button
                          label={translate('secondFactorUseSecurityKey', 'statements')}
                          onClick={() => addKey('securityKey')}
                          disabled={pending}
                          pending={pending}
                          baseColor
                        />

                        <Button
                          label={translate('secondFactorUsePasskey', 'statements')}
                          onClick={() => addKey('passkey')}
                          disabled={pending}
                          pending={pending}
                          baseColor
                        />
                      </>
                    )
                  : null}
              </>
            )}
      </Row>
    </Column>
  );
}

export default LoginStepEnroll;
